import { useEffect, useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import { gameTime, orderGames } from "./clock";
import "./GamesMenu.css";

const logoFor = (g, side) => g[`${side}_logo`] || (g[`${side}_code`] ? `https://a.espncdn.com/i/teamlogos/nba/500/${g[`${side}_code`].toLowerCase()}.png` : null);
const FILTERS = [["all", "ALL"], ["in", "LIVE"], ["pre", "PRE"], ["post", "FINAL"]];
// Competition buttons use the official league logo as the button background (user-provided assets).
const LEAGUES = [
  ["nba", "NBA", "/leagues/nba.jpg"],
  ["E", "EUROLEAGUE", "/leagues/euroleague.jpg"],
  ["U", "EUROCUP", "/leagues/eurocup.jpg"],
];

function GameRow({ g, selected, onSelect, value }) {
  const t = gameTime(g);
  const started = g.status === "in" || g.status === "post";
  return (
    <div role="button" tabIndex={0} className={`tv-game ${selected ? "is-selected" : ""} ${started ? "is-started" : ""} ${g.status === "in" ? "is-live" : ""} ${g.status === "post" ? "is-final" : ""}`} aria-pressed={selected}
      onClick={() => onSelect(g.id)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(g.id)} data-testid={`tv-game-${g.id}`}>
      <span className="tv-team"><img src={logoFor(g, "away")} alt="" /><b>{g.away_code}</b></span>
      <strong className="tv-score">{started ? `${g.away_pts ?? 0} – ${g.home_pts ?? 0}` : "vs"}</strong>
      <span className="tv-team tv-team-home"><b>{g.home_code}</b><img src={logoFor(g, "home")} alt="" /></span>
      <span className={`tv-clock ${t.critical ? "is-critical" : ""}`} data-testid={`tv-clock-${g.id}`}>{t.label}</span>
      {value > 0 && <span className="tv-value-badge" title={`${value} value ${value === 1 ? "bet" : "bets"} with a real edge`} data-testid={`tv-value-badge-${g.id}`}>{value} VALUE</span>}
    </div>
  );
}

export function GamesPanel({ games, selectedId, onSelect, valueCounts = {}, league = "nba", onLeague }) {
  const [now, setNow] = useState(Date.now());
  const [filter, setFilter] = useState("all");
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(t); }, []);
  const list = useMemo(() => orderGames(games, now).filter((g) => filter === "all" || g.status === filter), [games, now, filter]);
  return (
    <section className="tv-panel" aria-label="Games" data-testid="tv-games-panel">
      <div className="tv-menu-wrap">
        <div className="nbc-games-menu" aria-label="Today's games filters">
          <div className="nbc-games-title">
            <span className="nbc-ico"><CalendarDays size={15} /></span>
            <span>Today's games</span>
            <strong data-testid="tv-games-count">{games.length}</strong>
          </div>
          <div className="nbc-games-leagues" role="group" aria-label="Competition">
            {LEAGUES.map(([k, label, src]) => (
              <button key={k} type="button" style={{ backgroundImage: `url(${src})` }} className={`nbc-games-btn nbc-games-league ${league === k ? "is-active" : ""}`} onClick={() => onLeague?.(k)} data-testid={`tv-league-${k}`} aria-pressed={league === k} title={label}>
                <span className="nbc-sr">{label}</span>
              </button>
            ))}
          </div>
          <div className="nbc-games-statuses" role="group" aria-label="Game status">
            {FILTERS.map(([k, label]) => (
              <button key={k} type="button" className={`nbc-games-btn nbc-games-status ${filter === k ? "is-active" : ""}`} onClick={() => setFilter(k)} data-testid={`tv-filter-${k}`} aria-pressed={filter === k}>
                {label}<span>({k === "all" ? games.length : games.filter((g) => g.status === k).length})</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="tv-scroll">
        {list.map((g) => <GameRow key={g.id} g={g} selected={g.id === selectedId} onSelect={onSelect} value={valueCounts[g.id]} />)}
        {!list.length && <p className="tv-empty">No games in this filter.</p>}
      </div>
    </section>
  );
}
